import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { rankSlotMatches } from '../lib/slotSelection';
import { cn } from '@/lib/utils';

/*
 * The bulk-copy source picker: pick ONE slot out of everything the account owns, by typing.
 *
 * This is deliberately NOT a Radix Select with a search box wedged into it. Two properties of
 * Radix Select make that arrangement quietly broken, and both are in its installed source:
 *
 *   1. SelectContent renders its children inside SelectPrimitive.Viewport - the scroll container -
 *      so a "sticky" search row scrolls away with the list.
 *   2. Content force-focuses the selected item on open, and its onKeyDown swallows every
 *      single-character keypress into Radix's own typeahead. An <input> in there loses focus the
 *      moment the menu opens, and the keystrokes that do land jump item focus instead of typing.
 *
 * Popover + cmdk is the combination those primitives are designed for, and both were already
 * vendored in this repo (unused until now), so nothing new was installed.
 *
 * The item text is unchanged from the plain dropdown it replaces - slot id, label, @username - on
 * purpose: this adds a filter over the list people already know, it does not restyle it.
 */

/* cmdk filters in O(n) per keystroke and there is no virtualizer in this project, so the list is
   capped instead. Accounts here really do reach 10,100 slots; 200 rows is more than anyone scans
   and the count line below tells you when to keep typing rather than leaving you to guess. */
const MAX_RENDERED = 200;

export default function SlotSourcePicker({
  rows,
  value,
  onChange,
  disabled = false,
  id,
  className,
  placeholder = 'pick the ONE slot to copy from...',
  testId = 'slot-source-picker',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // `rows || []` inline would be a NEW array every render, so both memos below would recompute on
  // every keystroke of the parent - which is exactly the cost they exist to avoid.
  const all = useMemo(() => rows || [], [rows]);
  const selected = useMemo(() => all.find((r) => r.name === value) || null, [all, value]);
  const { rows: ranked, total } = useMemo(
    () => rankSlotMatches(all, query, MAX_RENDERED),
    [all, query]);

  /*
   * The current source is PINNED to the top rather than left to the cap. Without this, opening the
   * picker on slot4127 with an empty query shows rows 1-200, no checkmark anywhere, and no way to
   * see what the source currently is - the control would be lying about its own value.
   */
  const pinned = selected && !ranked.some((r) => r.name === selected.name) ? selected : null;
  const shown = pinned ? [pinned, ...ranked.slice(0, MAX_RENDERED - 1)] : ranked;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        // Reopening should offer the whole list again, not the last search someone left behind.
        if (!o) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-label={selected ? `Copy from ${selected.name}. Change the source slot.` : 'Choose the slot to copy from'}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border',
            'border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background',
            'focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className)}
          data-testid={testId}
        >
          <span className={cn('min-w-0 truncate', selected ? '' : 'text-muted-foreground')}>
            {selected ? selected.display : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[min(28rem,calc(100vw-2rem))] p-0"
        data-testid={`${testId}-content`}
      >
        {/* shouldFilter={false}: cmdk's own matcher scores its item VALUES, which would quietly be
            a second, different definition of "matches" alongside the page's search box. One
            predicate - slotMatches - answers for both. */}
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search slot id, label or @username..."
            data-testid={`${testId}-search`}
          />
          {/* Viewport-relative on a phone: a fixed 288px list plus the input, the footer and the
              trigger is taller than what is left of a 375x812 screen once the soft keyboard is up,
              so the results end up behind the keyboard. svh tracks the shrunk viewport. */}
          <CommandList className="max-h-[45svh] sm:max-h-72">
            <CommandEmpty>
              No slot matches &ldquo;{query.trim()}&rdquo;. Try a slot number, or part of the
              Instagram username.
            </CommandEmpty>
            <CommandGroup>
              {shown.map((r) => (
                <CommandItem
                  key={r.name}
                  value={r.name}
                  onSelect={() => { onChange(r.name); setOpen(false); }}
                  /* min-h-11 = 44px: this repo's own coarse-pointer target size. */
                  className={cn('min-h-11', pinned && r.name === pinned.name && 'border-b border-border')}
                  data-testid={`${testId}-item-${r.name}`}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0',
                    r.name === value ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 truncate">{r.display}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {/* Never truncate silently: a capped list that says nothing reads as "that slot does not
              exist" to the one person whose slot sits at number 4,000. */}
          {total > ranked.length && (
            <p
              className="border-t border-border px-3 py-2 text-xs text-muted-foreground tnum"
              data-testid={`${testId}-more`}
            >
              Showing the closest {ranked.length} of {total} matches - keep typing to narrow it down.
            </p>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
